import { useState, useEffect } from 'react';
import './PresentationPlayer.css';

export default function PresentationPlayer({ slides, title, onProgress }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [furthest, setFurthest] = useState(0);

  // Report how far the learner has got (slides seen / total) so lessons can require viewing every slide.
  useEffect(() => {
    const f = Math.max(furthest, currentSlide);
    if (f !== furthest) setFurthest(f);
    if (onProgress) onProgress(f + 1, slides.length);
  }, [currentSlide, slides.length]);

  const goToSlide = (n) => {
    if (n >= 0 && n < slides.length) {
      setCurrentSlide(n);
    }
  };

  const slide = slides[currentSlide];

  return (
    <div className="presentation-player">
      <div className="presentation-content">
        <div className="slide-header">
          <h2>{slide.title}</h2>
          {slide.subtitle && <p className="slide-subtitle">{slide.subtitle}</p>}
        </div>

        <div className="slide-body">
          {slide.type === 'text' && (
            <div className="slide-text">
              {slide.content.map((item, i) => (
                <div key={i} className={`content-item level-${item.level || 0}`}>
                  {item.bold ? <strong>{item.text}</strong> : item.text}
                </div>
              ))}
            </div>
          )}

          {slide.type === 'list' && (
            <ul className="slide-list">
              {slide.content.map((item, i) => (
                <li key={i} className={`list-item level-${item.level || 0}`}>
                  <span className="list-bullet">•</span>
                  {item.bold ? <strong>{item.text}</strong> : item.text}
                </li>
              ))}
            </ul>
          )}

          {slide.type === 'definition' && (
            <div className="slide-definition">
              {slide.content.map((item, i) => (
                <div key={i} className="definition-item">
                  <div className="definition-term">
                    <strong>{item.term}</strong>
                  </div>
                  <div className="definition-description">{item.description}</div>
                </div>
              ))}
            </div>
          )}

          {slide.type === 'comparison' && (
            <div className="slide-comparison">
              <div className="comparison-row">
                {slide.content.map((item, i) => (
                  <div key={i} className="comparison-column">
                    <div className="comparison-header">{item.header}</div>
                    <ul className="comparison-list">
                      {item.items.map((text, j) => (
                        <li key={j}>{text}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {slide.type === 'checklist' && (
            <ul className="slide-checklist">
              {slide.content.map((item, i) => (
                <li key={i} className="checklist-item">
                  <span className="checkmark">✓</span>
                  {item.bold ? <strong>{item.text}</strong> : item.text}
                </li>
              ))}
            </ul>
          )}
        </div>

        {slide.notes && (
          <div className="slide-notes">
            <strong>Key Points:</strong> {slide.notes}
          </div>
        )}
      </div>

      <div className="presentation-controls">
        <button
          onClick={() => goToSlide(currentSlide - 1)}
          disabled={currentSlide === 0}
          className="control-btn"
        >
          ← Previous
        </button>

        <div className="slide-indicator">
          {slides.map((_, i) => (
            <button
              key={i}
              className={`indicator ${i === currentSlide ? 'active' : ''}`}
              onClick={() => goToSlide(i)}
              title={`Slide ${i + 1}`}
            >
              {i + 1}
            </button>
          ))}
        </div>

        <button
          onClick={() => goToSlide(currentSlide + 1)}
          disabled={currentSlide === slides.length - 1}
          className="control-btn"
        >
          Next →
        </button>
      </div>

      <div className="slide-counter">
        Slide {currentSlide + 1} of {slides.length}
      </div>
    </div>
  );
}
